import type { ContextContent } from "$root/utils";
import { Dialog, DialogFooter, Icon, ScrollWrapper, type StylableProps } from "@mdotm/shadcn/legacy/components";
import { Button, type ButtonProps } from "@mdotm/shadcn/mdotm/components";
import { toClassName, useEventListener, useUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { observe, readAtom, useAtomState, WithAtoms } from "@mdotm/shadcn/legacy/reactivity";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { Map } from "immutable";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import ReactJson from "react-json-view";
import AuthorizationGuard from "./AuthorizationGuard";

export type DataDisplayOverlayProps = StylableProps & { dataSource?: string } & (
		| { data: any; dataProvider?: undefined }
		| { data?: undefined; dataProvider: () => any }
	);

type Overlay = { dataSource: string; dataProvider: () => any };

export const DataDisplayOverlayContext = createContext<{
	upsertOverlay(id: string, props: Overlay): void;
	removeOverlay(id: string): void;
	toggleVisibility(id: string): void;
} | null>(null);

function DataDisplayOverlayInner({
	overlays,
	onlyVisibleId,
	setOnlyVisibleId,
	isOverlayOpen,
	setIsOverlayOpen,
}: {
	overlays: Map<string, Overlay>;
	onlyVisibleId: string | null;
	setOnlyVisibleId(param: string | null): void;
	isOverlayOpen: boolean;
	setIsOverlayOpen(param: boolean): void;
}) {
	const sortedVisibleOverlays = useMemo(() => {
		const visibleOverlay = onlyVisibleId && overlays.get(onlyVisibleId);
		return visibleOverlay
			? [{ id: onlyVisibleId, ...visibleOverlay }]
			: Array.from(overlays.entries())
					.map(([id, { dataProvider, dataSource }]) => ({ id, dataProvider, dataSource }))
					.sort(builtInSortFnFor("dataSource"));
	}, [onlyVisibleId, overlays]);

	const [isClickable, setIsClickable] = useState(false);
	const [asModal, setAsModal] = useState(false);

	useEffect(() => {
		if (!isOverlayOpen) {
			setOnlyVisibleId(null);
		}
	}, [isOverlayOpen, setOnlyVisibleId]);

	useEventListener(window, "keydown", (e) => {
		if (e.key === "Alt" && !asModal) {
			e.preventDefault();
			setIsClickable(true);
		}
	});
	useEventListener(window, "keydown", (e) => {
		if (e.key === "F6") {
			e.preventDefault();
			setIsOverlayOpen(!isOverlayOpen);
		}
	});
	useEventListener(window, "keyup", () => setIsClickable(false));

	const [isCopied, setIsCopied] = useState(false);
	// The effect owns the reset timer, so unmount and a re-copy both clear it.
	useEffect(() => {
		if (!isCopied) {
			return;
		}
		const reset = setTimeout(() => setIsCopied(false), 1000);
		return () => clearTimeout(reset);
	}, [isCopied]);
	const copyToClipboard = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(
				JSON.stringify(
					Object.fromEntries(sortedVisibleOverlays.map(({ dataSource, dataProvider }) => [dataSource, dataProvider()])),
				),
			);
			setIsCopied(true);
		} catch (e) {
			console.log(e);
		}
	}, [sortedVisibleOverlays]);
	const copyToClipboardButton = useCallback(
		(size?: ButtonProps["size"]) => (
			<Button size={size} palette={isCopied ? "primary" : "tertiary"} onClickAsync={copyToClipboard}>
				{isCopied ? "Copied!" : "Copy"}
			</Button>
		),
		[copyToClipboard, isCopied],
	);

	const viewer = useMemo(
		() =>
			sortedVisibleOverlays.map(({ id, dataSource, dataProvider }) => (
				<div key={id}>
					<div>
						<span className={toClassName({ "font-bold": true, "bg-blue-300/80": !isClickable })}>{dataSource}</span>
					</div>
					<div>
						<ReactJson src={dataProvider()} />
					</div>
				</div>
			)),
		[isClickable, sortedVisibleOverlays],
	);

	return (
		<AuthorizationGuard requiredRole="ROOT">
			{() => (
				<>
					{asModal ? (
						<Dialog
							show={isOverlayOpen}
							size="xlarge"
							header="Data"
							footer={
								<DialogFooter
									neutralAction={copyToClipboardButton()}
									secondaryAction={<Button onClick={() => setAsModal(false)}>Show as Overlay</Button>}
									primaryAction={<Button onClick={() => setIsOverlayOpen(false)}>Close</Button>}
								/>
							}
							onClose={() => setIsOverlayOpen(false)}
							startShadow={false}
						>
							{viewer}
						</Dialog>
					) : (
						isOverlayOpen &&
						createPortal(
							<div
								className={toClassName({
									"fixed inset-0 flex flex-col": true,
									"pointer-events-none": !isClickable,
									"bg-white/90": isClickable,
								})}
								style={{ zIndex: 2147483648 }}
							>
								<div className="absolute right-0 top-0 z-10">
									<button
										type="button"
										onClick={() => setIsOverlayOpen(false)}
										className="text-white bg-black pointer-events-auto"
									>
										<Icon icon="Close" size={32} />
									</button>
								</div>
								<div className="relative z-0">
									<span className="bg-black/40 text-white">
										HINT: Use Alt+Click to interact with the JSON viewer. When Alt is NOT pressed, your clicks are
										propagated to the underlying elements.
									</span>
									<div className="py-2 flex space-x-4">
										{copyToClipboardButton("x-small")}
										<Button size="x-small" onClick={() => setAsModal(true)}>
											Show as Dialog
										</Button>
									</div>
								</div>
								<div
									className={toClassName({
										"flex-1 justify-items-stretch flex-col flex min-h-0 relative z-0": true,
										"[&_span]:bg-blue-300/80": !isClickable,
									})}
								>
									<ScrollWrapper>{viewer}</ScrollWrapper>
								</div>
							</div>,
							document.body,
						)
					)}
				</>
			)}
		</AuthorizationGuard>
	);
}

export function DataDisplayOverlayProvider({ children }: { children: ReactNode }): React.JSX.Element {
	const [overlays$, setOverlays] = useAtomState(() => Map<string, Overlay>());
	const [onlyVisibleId$, setOnlyVisibleId] = useAtomState<string | null>(null);

	const [isOverlayOpen$, setIsOverlayOpen] = useAtomState(false);

	useEffect(() => {
		const unsubscribe = observe((get) => {
			if (!get(isOverlayOpen$)) {
				setOnlyVisibleId(null);
			}
		});
		return unsubscribe;
	}, [isOverlayOpen$, setOnlyVisibleId]);

	const ctx: ContextContent<typeof DataDisplayOverlayContext> = useMemo(
		() => ({
			removeOverlay: (id) => setOverlays((o) => o.remove(id)),
			upsertOverlay: (id, overlay) => setOverlays((o) => o.set(id, overlay)),
			toggleVisibility: (id) => {
				if (readAtom(onlyVisibleId$) === id) {
					setOnlyVisibleId(null);
					setIsOverlayOpen(false);
				} else {
					setOnlyVisibleId(id);
					setIsOverlayOpen(true);
				}
			},
		}),
		[onlyVisibleId$, setIsOverlayOpen, setOnlyVisibleId, setOverlays],
	);

	return (
		<>
			<WithAtoms atoms={[overlays$, onlyVisibleId$, isOverlayOpen$]}>
				{([overlays, onlyVisibleId, isOverlayOpen]) => (
					<DataDisplayOverlayInner
						{...{
							overlays,
							isOverlayOpen,
							setIsOverlayOpen,
							onlyVisibleId,
							setOnlyVisibleId,
						}}
					/>
				)}
			</WithAtoms>
			<DataDisplayOverlayContext.Provider value={ctx}>{children}</DataDisplayOverlayContext.Provider>
		</>
	);
}

export function DataDisplayOverlay({
	dataProvider: optionalDataProvider,
	dataSource,
	data: optionalData,
	className,
	style,
}: DataDisplayOverlayProps): ReactNode {
	const dataProvider = useMemo(
		() => optionalDataProvider ?? (() => optionalData),
		[optionalData, optionalDataProvider],
	);
	const ctx = useContext(DataDisplayOverlayContext);
	const ctxRef = useUnsafeUpdatedRef(ctx);
	const id = useUniqueDOMId();
	useEffect(() => {
		ctxRef.current?.upsertOverlay(id, { dataSource: dataSource ?? id, dataProvider });
	}, [ctxRef, dataProvider, dataSource, id]);
	useEffect(() => {
		// eslint-disable-next-line react-hooks/exhaustive-deps
		return () => ctxRef.current?.removeOverlay(id);
	}, [ctxRef, id]);

	return !ctx ? null : (
		<AuthorizationGuard requiredRole="ROOT">
			{() => (
				<Button
					palette="inverted"
					size="x-small"
					color="info"
					onClick={() => ctx.toggleVisibility(id)}
					className={className}
					style={style}
				>
					Toggle Data [{dataSource ?? ""}]
				</Button>
			)}
		</AuthorizationGuard>
	);
}
